import React, {useCallback, useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {Dropdown, Space} from 'antd';
import {LoadingOutlined} from '@ant-design/icons';
import {openBankingActions} from '../../../../state/actions/openBanking.actions';
import {useCanConnectBank, useLoadOpenBankingAccounts, useWireDetails} from '../../../../hooks';
import {amountHelpers, cardsHelpers, transactionsHelpers} from '../../../../utils/helpers';
import {DEFAULT_CURRENCY_CODE} from '../../../../constants/amount.constants';
import BalanceDetails from '../BalanceDetails';
import {
  StyledAccountBalanceDropdown,
  StyledAccountBalanceDropdownOverlay,
  StyledAccountBalanceDropdownAccountsList,
  StyledAccountBalanceDropdownAccountItem,
  StyledAccountBalanceDropdownOptionLabel,
  StyledAccountBalanceDropdownOptionAmount,
  StyledAccountBalanceDropdownFooter,
  StyledAccountBalanceDropdownSyncStatus,
  StyledAccountBalanceDropdownConnectBankLink,
  StyledAccountBalanceDropdownDivider,
  StyledAccountBalanceDropdownError,
} from './StyledAccountBalanceDropdown';
import IconWrapper from '../../../IconWrapper';
import AddFundsDetails from '../../SubscriptionsPage/AddFundsDetails';
import {ChevronDownIcon} from '../../../../icons';
import {useOpenBankingAccounts} from '../../../../hooks/banking/useOpenBankingAccounts';
import {useOpenBankingAccountAccessIds} from '../../../../hooks/banking/useOpenBankingAccountAccessIds';

const AccountBalanceDropdown = ({onAccountChange, showTreezorBalance = true}) => {
  const canConnectBank = useCanConnectBank();
  const [t] = useTranslation('main');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dispatch = useDispatch();

  const accounts = useOpenBankingAccounts();
  const accountAccessIds = useOpenBankingAccountAccessIds();
  const isLoading = useSelector((state) => state.openBanking?.isLoading ?? false);
  const isCreatingConnection = useSelector((state) => state.openBanking?.isCreatingConnection ?? false);
  const connectionCreateError = useSelector((state) => state.openBanking?.connectionCreateError ?? null);
  const balanceDetails = useSelector((state) => state.transaction?.balanceDetails ?? {});
  const currentCurrency = useSelector((state) => state.company?.currentCurrency ?? null);
  const wireDetails = useWireDetails();

  const loadAccounts = useLoadOpenBankingAccounts();

  // Only load once we know which accounts this user may access; an empty list would
  // otherwise trigger an unscoped fetch of all accounts (H1MVP-4452). Key on the ids
  // array itself (not just its length) so the load also re-fires when a company switch
  // swaps in a same-size but different access list (H1MVP-5024).
  useEffect(() => {
    if (accounts.length || !accountAccessIds.length) return;
    loadAccounts();
  }, [accountAccessIds]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalValue = useMemo(() => {
    const obSum = accounts.reduce((acc, account) => acc + (Number(account.balance) || 0), 0);
    const treezorValue = showTreezorBalance ? (Number(balanceDetails?.value) || 0) : 0;
    const total = obSum + treezorValue;
    const currency = currentCurrency || balanceDetails?.currency || DEFAULT_CURRENCY_CODE;
    return amountHelpers.getAmountWithCurrencyCode(total, {currencyCode: currency});
  }, [accounts, balanceDetails, currentCurrency, showTreezorBalance]);

  const handleAccountClick = useCallback((account) => {
    onAccountChange?.(account.id);
  }, [onAccountChange]);

  const handleConnectBank = useCallback((e) => {
    e.preventDefault();
    dispatch(openBankingActions.createConnection());
    setDropdownOpen(false);
  }, [dispatch]);

  const withlessBalance = useMemo(() => {
    return transactionsHelpers.getBalanceDisplayValue(balanceDetails, currentCurrency || DEFAULT_CURRENCY_CODE);
  }, [balanceDetails, currentCurrency]);

  const showFooter = canConnectBank || !!connectionCreateError || isLoading;

  // OB accounts still paging in, or a Treezor balance is expected but hasn't settled
  // (loaded, or requested-and-failed/SCA-gated) yet — either way the total shown is
  // provisional, so flag it rather than let the user read it as final.
  const isBalanceLoading = isLoading || (showTreezorBalance && !balanceDetails.isLoaded && !balanceDetails.isRequested);

  const dropdownOverlay = useMemo(() => (
    <StyledAccountBalanceDropdownOverlay>
      <StyledAccountBalanceDropdownAccountsList>
        {showTreezorBalance && (
          <StyledAccountBalanceDropdownAccountItem key='withless'>
            <StyledAccountBalanceDropdownOptionLabel>{t('withless')}</StyledAccountBalanceDropdownOptionLabel>
            <Space size='small'>
              <AddFundsDetails
                details={wireDetails}
                buttonProps={{size: 'small', $compact: true, onClick: () => setDropdownOpen(false)}}
              />
              <StyledAccountBalanceDropdownOptionAmount>{withlessBalance}</StyledAccountBalanceDropdownOptionAmount>
            </Space>
          </StyledAccountBalanceDropdownAccountItem>
        )}
        {accounts.map((account) => (
          <StyledAccountBalanceDropdownAccountItem
            key={account.id}
            onClick={() => handleAccountClick(account)}
          >
            <StyledAccountBalanceDropdownOptionLabel>
              {account.name} *{cardsHelpers.hideCardNumber(account.iban || account?.extra?.iban, 4)}
            </StyledAccountBalanceDropdownOptionLabel>
            <StyledAccountBalanceDropdownOptionAmount>
              {amountHelpers.getAmountWithCurrencyCode(account.balance, {currencyCode: account.currency_code})}
            </StyledAccountBalanceDropdownOptionAmount>
          </StyledAccountBalanceDropdownAccountItem>
        ))}
      </StyledAccountBalanceDropdownAccountsList>
      {showFooter && (
        <>
          <StyledAccountBalanceDropdownDivider />
          <StyledAccountBalanceDropdownFooter>
            <div>
              {canConnectBank && (
                <StyledAccountBalanceDropdownConnectBankLink onClick={handleConnectBank}>
                  {isCreatingConnection && <LoadingOutlined spin style={{marginRight: 4}} />}
                  + {t('connectBank')}
                </StyledAccountBalanceDropdownConnectBankLink>
              )}
              {connectionCreateError && (
                <StyledAccountBalanceDropdownError>
                  <span>{connectionCreateError}</span>
                  <StyledAccountBalanceDropdownConnectBankLink onClick={handleConnectBank}>
                    {t('tryAgain')}
                  </StyledAccountBalanceDropdownConnectBankLink>
                </StyledAccountBalanceDropdownError>
              )}
            </div>
            {isLoading && (
              <StyledAccountBalanceDropdownSyncStatus>
                <LoadingOutlined spin />
                <span>{t('syncing')}</span>
              </StyledAccountBalanceDropdownSyncStatus>
            )}
          </StyledAccountBalanceDropdownFooter>
        </>
      )}
    </StyledAccountBalanceDropdownOverlay>
  ), [accounts, wireDetails, withlessBalance, showTreezorBalance, showFooter, isLoading, isCreatingConnection, connectionCreateError, canConnectBank, t, handleAccountClick, handleConnectBank]);

  const totalBalanceValue = (
    <Space size='small'>
      {totalValue}
      {isBalanceLoading && <LoadingOutlined spin />}
    </Space>
  );

  return (
    <Dropdown
      popupRender={() => dropdownOverlay}
      trigger={['click']}
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
    >
      <StyledAccountBalanceDropdown>
        <BalanceDetails
          name={t('totalBalance')}
          value={totalBalanceValue}
        />
        <IconWrapper size={24} className='account-balance-dropdown-icon'><ChevronDownIcon /></IconWrapper>
      </StyledAccountBalanceDropdown>
    </Dropdown>
  );
};

AccountBalanceDropdown.propTypes = {
  onAccountChange: PropTypes.func,
  showTreezorBalance: PropTypes.bool,
};

export default AccountBalanceDropdown;
